import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Loader2, Check, Lightbulb } from 'lucide-react';

const EditableLightingTab = ({ event, onUpdate }) => {
  const [lightingNotes, setLightingNotes] = useState(event?.lighting_notes || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // Track pending changes and backup timer
  const pendingChanges = useRef({});
  const backupTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);

  // Only sync with parent on initial mount
  useEffect(() => {
    if (isInitialMount.current) {
      setLightingNotes(event?.lighting_notes || '');
      isInitialMount.current = false;
    }
  }, [event?.lighting_notes]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (backupTimeoutRef.current) {
        clearTimeout(backupTimeoutRef.current);
      }
    };
  }, []);

  // Perform the actual save
  const doSave = useCallback(async () => {
    if (Object.keys(pendingChanges.current).length === 0) return;
    
    setSaving(true);
    const changesToSave = { ...pendingChanges.current };
    pendingChanges.current = {};
    
    try {
      await onUpdate(changesToSave);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save:', error);
      pendingChanges.current = { ...changesToSave, ...pendingChanges.current };
    } finally {
      setSaving(false);
    }
  }, [onUpdate]);

  // Queue a change
  const queueChange = useCallback((updates) => {
    pendingChanges.current = { ...pendingChanges.current, ...updates };
    
    if (backupTimeoutRef.current) {
      clearTimeout(backupTimeoutRef.current);
    }
    
    backupTimeoutRef.current = setTimeout(() => {
      doSave();
    }, 1000);
  }, [doSave]);

  // Save immediately (for blur events)
  const saveNow = useCallback(() => {
    if (backupTimeoutRef.current) {
      clearTimeout(backupTimeoutRef.current);
    }
    doSave();
  }, [doSave]);

  const handleNotesChange = (value) => {
    setLightingNotes(value);
    queueChange({ lighting_notes: value });
  };

  return (
    <Card className="border-border/50 bg-card/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-yellow-400" />
            Lighting
          </CardTitle>
          <div className="flex items-center h-6">
            {saving && (
              <Badge variant="outline" className="border-blue-500/30 bg-blue-500/10 text-blue-400">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Saving...
              </Badge>
            )}
            {saved && !saving && (
              <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                <Check className="h-3 w-3 mr-1" />
                Saved
              </Badge>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div>
          <Label className="text-sm text-muted-foreground uppercase tracking-wider mb-2 block">
            Lighting Notes & Requirements
          </Label>
          <Textarea
            value={lightingNotes}
            onChange={(e) => handleNotesChange(e.target.value)}
            onBlur={saveNow}
            placeholder="Enter lighting requirements, equipment notes, special effects, etc..."
            rows={8}
            className="bg-background/50"
            data-testid="lighting-notes-input"
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default EditableLightingTab;
